import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtNum, fmtRiyal } from "@/utils/toEnglishDigits";
import CarImagesGrid from "@/components/CarImagesGrid";
import NumberField from "@/components/NumberField";
import { Car as CarIcon, Plus, Pencil, Trash, Image as ImageIcon, Users as UsersIcon, CarProfile } from "@phosphor-icons/react";

const emptyCar = () => ({
  id: "",
  name_ar: "",
  name_en: "",
  capacity: 4,
  base_price: 0,
  home_visible: true,
  home_order: 100,
  images: [],
});

function CarModal({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(emptyCar());
  const [saving, setSaving] = useState(false);
  const [imagesBusy, setImagesBusy] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) {
      if (initial) {
        const legacy = initial.image_url || initial.image_base64;
        const imgs = Array.isArray(initial.images) && initial.images.length
          ? initial.images
          : (legacy ? [legacy] : []);
        setForm({
          id: initial.id,
          name_ar: initial.name_ar || "",
          name_en: initial.name_en || "",
          capacity: initial.capacity || 4,
          base_price: initial.base_price || 0,
          home_visible: (initial.home_visible ?? initial.active) !== false,
          home_order: initial.home_order ?? initial.order ?? 100,
          images: imgs,
        });
      } else {
        setForm(emptyCar());
      }
    }
  }, [open, initial]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (imagesBusy) {
      toast.error("يجرى ضغط الصور — انتظر لحظات قبل الحفظ");
      return;
    }
    if (!form.id.trim()) return toast.error("المعرّف (id) مطلوب");
    if (!form.name_ar.trim()) return toast.error("الاسم بالعربية مطلوب");
    if (!form.capacity || form.capacity < 1 || form.capacity > 60) {
      return toast.error("عدد الركاب يجب أن يكون بين 1 و 60");
    }
    setSaving(true);
    try {
      const payload = {
        id: form.id,
        name_ar: form.name_ar,
        name_en: form.name_en,
        capacity: form.capacity,
        base_price: form.base_price,
        home_visible: form.home_visible,
        home_order: form.home_order,
        images: form.images || [],
      };
      if (isEdit) {
        await api.patch(`/admin/cars/${initial.id}`, payload);
        toast.success("تم تحديث السيارة");
      } else {
        await api.post("/admin/cars", payload);
        toast.success("تم إضافة السيارة");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      dir="rtl"
      data-testid="car-modal"
    >
      <div className="bg-card border border-primary/25 rounded-2xl w-full max-w-2xl gold-glow max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-primary/15 flex items-center justify-between sticky top-0 bg-card z-10">
          <h3 className="text-primary font-bold text-lg">{isEdit ? "تعديل السيارة" : "سيارة جديدة"}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="car-modal-close">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {/* 8-image grid */}
          <CarImagesGrid
            value={form.images}
            onChange={(imgs) => set("images", imgs)}
          />

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-white/60 mb-1 text-right">المعرّف (id) *</div>
              <input
                required
                value={form.id}
                onChange={(e) => set("id", e.target.value.trim().toLowerCase())}
                className="mz-input"
                dir="ltr"
                placeholder="lexus"
                disabled={isEdit}
                data-testid="car-id"
              />
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-1 text-right">🧍 عدد الركاب المسموح به *</div>
              <NumberField
                required
                min={1}
                max={60}
                value={form.capacity}
                onChange={(v) => set("capacity", v === "" ? "" : Number(v))}
                className="mz-input"
                testId="car-capacity"
                placeholder="مثال: 4"
              />
              <div className="text-[10px] text-white/40 mt-1 text-right">من 1 إلى 60 (صلاحية الأدمن فقط)</div>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-white/60 mb-1 text-right">الاسم بالعربية *</div>
              <input required value={form.name_ar} onChange={(e) => set("name_ar", e.target.value)} className="mz-input" data-testid="car-name-ar" />
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-1 text-right">الاسم بالإنجليزية</div>
              <input value={form.name_en} onChange={(e) => set("name_en", e.target.value)} className="mz-input" dir="ltr" data-testid="car-name-en" />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-white/60 mb-1 text-right">ترتيب العرض</div>
              <NumberField min={1} value={form.home_order} onChange={(val) => set("home_order", val === "" ? "" : Number(val))} className="mz-input" testId="car-order" />
            </label>
            <label className="flex items-end gap-2 pb-2">
              <input type="checkbox" checked={form.home_visible} onChange={(e) => set("home_visible", e.target.checked)} className="w-4 h-4 accent-[#C9A961]" data-testid="car-visible" />
              <span className="text-sm text-white/80">مرئية للعملاء</span>
            </label>
          </div>

          <div className="rounded-lg bg-primary/5 border border-primary/20 px-3 py-2 text-[11px] text-white/60 leading-relaxed">
            💰 السعر الأساسي يُدار الآن من صفحة <span className="text-primary font-bold">فرز أرباح السيارات</span>.
          </div>

          <div className="flex gap-3 pt-3">
            <button type="submit" disabled={saving || imagesBusy} className="flex-1 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold disabled:opacity-60" data-testid="car-save">
              {saving ? "جارٍ الحفظ…" : imagesBusy ? "يجرى ضغط الصور…" : (isEdit ? "حفظ التعديلات" : "إضافة")}
            </button>
            <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl border border-white/20 text-white/70">إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CarsPage() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fleetCounts, setFleetCounts] = useState({});
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [carsRes, countsRes] = await Promise.all([
        api.get("/cars"),
        api.get("/admin/fleet/counts").catch(() => ({ data: { counts: {} } })),
      ]);
      setCars(carsRes.data);
      setFleetCounts(countsRes.data?.counts || {});
    } catch {
      toast.error("تعذر تحميل السيارات");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (c) => {
    const ok = await confirm({
      title: "حذف السيارة",
      message: `سيتم حذف "${c.name_ar}" من قائمة السيارات المتاحة.`,
      confirmText: "نعم، احذف",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/cars/${c.id}`);
      toast.success("تم الحذف");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">السيارات</h1>
            <p className="text-white/50 text-sm mt-1"><span className="num">{fmtNum(cars.length)}</span> نوع سيارة</p>
          </div>
          <button
            onClick={() => { setEditing(null); setModalOpen(true); }}
            data-testid="add-car"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-[#E1C484] text-primary-foreground rounded-xl font-bold"
          >
            <Plus size={18} weight="bold" /> سيارة جديدة
          </button>
        </div>

        <div className="mb-5 rounded-xl border border-primary/25 bg-primary/5 px-4 py-3 text-xs text-white/70 leading-relaxed" data-testid="cars-page-hint">
          💡 يمكنك تعديل السعة والصورة لكل سيارة، أو حذفها. <span className="text-primary font-bold">(السعر الأساسي يُعدَّل من صفحة فرز أرباح السيارات)</span>
        </div>

        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
        ) : cars.length === 0 ? (
          <div className="text-center py-16 text-white/50 bg-card border border-primary/15 rounded-2xl">لا توجد سيارات</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cars.map((c, i) => (
              <div
                key={c.id}
                data-testid={`car-${c.id}`}
                className="fadeup bg-card border border-primary/15 rounded-2xl overflow-hidden hover:border-primary/40 transition-all"
                style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}
              >
                <Link
                  to={`/car/${encodeURIComponent(c.id)}`}
                  data-testid={`open-car-${c.id}`}
                  className="block aspect-video bg-[#0B1A3A] overflow-hidden relative cursor-pointer group"
                >
                  {c.image_url ? (
                    <img src={c.image_url} alt={c.name_ar} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ opacity: 0.9 }} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-primary/30">
                      <CarIcon size={50} weight="duotone" />
                    </div>
                  )}
                  {(c.images_count ?? 0) > 0 && (
                    <span
                      className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#050A15]/75 border border-primary/40 text-primary num"
                      dir="ltr"
                      data-testid={`car-${c.id}-count`}
                    >
                      <ImageIcon size={10} weight="fill" /> {fmtNum(c.images_count)}/{fmtNum(8)}
                    </span>
                  )}
                  {/* Always-visible "تفاصيل" pill — on ALL cards */}
                  <span
                    className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1 rounded-full bg-primary/20 border border-primary/60 text-primary backdrop-blur transition-all group-hover:bg-primary group-hover:text-primary-foreground"
                    data-testid={`car-${c.id}-details-pill`}
                  >
                    <CarIcon size={11} weight="fill" /> تفاصيل
                  </span>
                  {!(c.home_visible ?? c.active) && (
                    <span className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full bg-white/10 border border-white/25 text-white/70">
                      مخفية
                    </span>
                  )}
                </Link>
                <div className="p-4">
                  <div>
                    <div className="text-white font-bold">{c.name_ar}</div>
                    <div className="text-white/40 text-[10px] uppercase tracking-widest mt-0.5">{c.name_en || c.id}</div>
                  </div>
                  <div className="flex items-center gap-3 mt-3 text-white/50 text-xs">
                    <span className="inline-flex items-center gap-1"><UsersIcon size={12} weight="duotone" /> <span className="num">{fmtNum(c.capacity || 4)}</span> مقاعد</span>
                    <span>·</span>
                    <span>ترتيب: <span className="num">{fmtNum(c.home_order ?? c.order ?? 999)}</span></span>
                  </div>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-primary/10">
                    <button
                      onClick={() => { setEditing(c); setModalOpen(true); }}
                      data-testid={`edit-car-${c.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 text-xs font-bold"
                    >
                      <Pencil size={13} weight="bold" /> تعديل + صورة
                    </button>
                    <button
                      onClick={() => remove(c)}
                      data-testid={`delete-car-${c.id}`}
                      className="p-2 rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10"
                    >
                      <Trash size={13} weight="bold" />
                    </button>
                  </div>
                  {/* Fleet register — plates registered under this car_type */}
                  <Link
                    to={`/admin/fleet/${encodeURIComponent(c.id)}`}
                    data-testid={`car-${c.id}-fleet-btn`}
                    className="mt-3 flex items-center justify-between gap-2 rounded-xl border border-primary/40 bg-primary/10 hover:bg-primary/20 px-3 py-2.5 transition-all group"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary/20 border border-primary/40 text-primary shrink-0">
                        <CarProfile size={16} weight="duotone" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-primary text-xs font-bold truncate">
                          السيارات المسجّلة تحت {c.name_ar}
                        </div>
                        <div className="text-white/50 text-[10px] mt-0.5 num">
                          {fmtNum(fleetCounts[c.id] || 0)} سيارة · اضغط لعرض اللوحات
                        </div>
                      </div>
                    </div>
                    <span
                      data-testid={`car-${c.id}-fleet-count`}
                      className="shrink-0 inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-primary text-primary-foreground text-[11px] font-bold num"
                    >
                      {fmtNum(fleetCounts[c.id] || 0)}
                    </span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <CarModal open={modalOpen} initial={editing} onClose={() => setModalOpen(false)} onSaved={load} />
    </div>
  );
}
